(() => {
  const CNSTRC = {
    DEFAULT_POD: "minicart-upsell",
    SECTION: "Products",
    CACHE_TTL: 5 * 60 * 1000,
  };

  const escapeHTML = (s = "") =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

  const bySel = (sel, root = document) => root.querySelector(sel);

  const imageDimsFor = (orientation) => {
    switch (orientation) {
      case "square":
        return { w: 460, h: 460 };
      case "landscape":
        return { w: 200, h: 160 };
      case "portrait":
        return { w: 160, h: 200 };
      default:
        return { w: 460, h: 460 };
    }
  };

  const money = (cents, currency = window.theme?.fakeDiscount?.currentMarket?.currency || "USD") => {
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Math.round(cents) || 0) / 100);
    } catch {
      return `${(Math.round(cents) || 0) / 100}`;
    }
  };

  const productHasTag = (product, requiredTag) => {
    if (!product.tags || !requiredTag) return false;
    const tags = Array.isArray(product.tags) ? product.tags : (product.tags || "").split(",").map((tag) => tag.trim());
    return tags.includes(requiredTag);
  };

  class RecsService {
    constructor(clientResolver) {
      this.clientResolver = clientResolver;
      this.cache = new Map();
      this.inflight = new Map();
    }

    _key(podId, params) {
      const { numResults = 8, filters, item_id } = params || {};
      return `${podId}:${numResults}:${item_id || "no-item"}:${JSON.stringify(filters || {})}`;
    }

    async get(podId, params = {}) {
      const key = this._key(podId, params);

      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.ts < CNSTRC.CACHE_TTL) {
        return cached;
      }

      if (this.inflight.has(key)) return this.inflight.get(key);

      const p = this._fetch(podId, params)
        .then((res) => {
          this.cache.set(key, { ...res, ts: Date.now() });
          this.inflight.delete(key);
          return this.cache.get(key);
        })
        .catch((e) => {
          this.inflight.delete(key);
          throw e;
        });

      this.inflight.set(key, p);
      return p;
    }

    async _fetch(podId, params) {
      const client = await this.clientResolver();
      const {
        numResults = 8,
        filters,
        fmt_options = {},
        variationsMap,
        hiddenFields,
        preFilterExpression,
        section = CNSTRC.SECTION,
        itemIds,
      } = params;

      const networkParameters = { timeout: 5000 };

      const defaultFmtOptions = {
        show_hidden_fields: false,
        show_hidden_facets: false,
        fields: [
          "data.handle",
          "data.title",
          "data.price",
          "data.compareAtPrice",
          "data.image_url",
          "data.inventory_quantity",
          "data.group_ids",
          "data.preorder_tag",
          "data.waterproof_tag",
          "data.bestseller_tag",
          "data.new_tag",
          "data.mediaImages",
          "data.url",
          "data.tags",
          "data.vendor",
          "data.product_type",
          "data.options",
          "data.fake_discount_percentage",
          "data.fake_discount_message",
          "data.created_at",
          "data.updated_at",
          "data.published_at",
          "data.created_date",
          "variations",
        ],
        ...fmt_options,
      };

      // Build recommendation request parameters
      const recommendationParams = {
        numResults,
        section,
        filters,
        fmt_options: defaultFmtOptions,
        variationsMap,
        hiddenFields,
        preFilterExpression,
      };

      // Add item_id if provided for item-based recommendations
      if (itemIds) {
        recommendationParams.itemIds = itemIds;
      }

      const response = await client.recommendations.getRecommendations(podId, recommendationParams, networkParameters);
      const results = response?.response?.results ?? [];
      const resultId = response?.result_id || (results[0] && results[0].result_id) || "";
      const total = response?.response?.total_num_results ?? results.length;

      return { data: results, meta: { resultId, total } };
    }
  }

  const toCardModel = (item) => {
    const data = item.data || {};
    const variations = item.variations || [];
    const title = item.value || data.title || "Product";
    const priceCents = Math.round((data.price || 0) * 100);
    const compareCents = Math.round((data.compareAtPrice || 0) * 100);

    // Transform URL for current market
    let url = data.url || (data.handle ? `/products/${data.handle}` : "");
    if (window.constructorRecommendations && typeof window.constructorRecommendations.transformUrlForMarket === "function") {
      url = window.constructorRecommendations.transformUrlForMarket(url);
    }

    let handle = null;
    if (data.url) {
      const urlParts = data.url.split("/products/");
      if (urlParts.length > 1) {
        handle = urlParts[1].split("?")[0].split("#")[0];
      }
    }
    if (!handle) {
      handle =
        data.handle ||
        data.slug ||
        data.shopify_handle ||
        item.value ||
        (data.id && typeof data.id === "string" && /[a-zA-Z-]/.test(data.id) ? data.id : null);
    }

    const options = (() => {
      const optionsMap = new Map();
      variations.forEach((variation) => {
        const varData = variation.data || {};
        if (varData.color) {
          if (!optionsMap.has("Color")) {
            optionsMap.set("Color", { name: "Color", values: new Set() });
          }
          optionsMap.get("Color").values.add(varData.color);
        }
      });
      return Array.from(optionsMap.entries()).map(([name, option]) => ({
        name,
        values: Array.from(option.values),
      }));
    })();

    const variants =
      variations?.map((v) => {
        const varData = v.data || {};
        const variantTitle = varData.title || v.value || "Variant";

        return {
          id: varData.variation_id || v.id,
          title: variantTitle,
          price: Math.round((varData.price || data.price || 0) * 100),
          compare_at_price: Math.round((varData.compareAtPrice || data.compareAtPrice || 0) * 100),
          inventory_quantity: varData.inventory_quantity || 0,
          available: (varData.inventory_quantity || 0) > 0,
          image_url: varData.image_url,
          color: varData.color,
          size: varData.size,
          discountPercentage: varData.discountPercentage || 0,
        };
      }) || [];

    return {
      id: data.id || item.id || handle,
      handle,
      title,
      price: priceCents,
      compare_at_price: compareCents,
      featured_image: {
        alt: title,
        src: data.image_url || "",
      },
      images: (() => {
        const rawMedia = data.mediaImages;
        // Temporary logging to debug mediaImages type issues
        if (rawMedia && !Array.isArray(rawMedia)) {
          console.log("mediaImages type:", typeof rawMedia, "value:", rawMedia, "for product:", title);
        }
        const mediaArray = Array.isArray(rawMedia) ? rawMedia : rawMedia ? [rawMedia] : [];
        return mediaArray.map((img) => ({
          alt: img?.altText || title,
          src: img?.transformedSrc || img?.src || "",
        }));
      })(),
      available: (data.inventory_quantity || 0) > 0,
      vendor: data.vendor || "",
      product_type: data.product_type || "",
      has_only_default_variant: variations.length <= 1,
      options,
      variants,
      tags: [
        ...(data.group_ids || []),
        ...(data.preorder_tag ? ["preorder"] : []),
        ...(data.waterproof_tag ? ["waterproof"] : []),
        ...(data.bestseller_tag ? ["bestseller", "bestsellers"] : []),
        ...(data.new_tag ? ["new"] : []),
        ...(data.low_inventory_tag ? ["low-inventory"] : []),
      ],
      fakeDiscountPercentage: data.fake_discount_percentage || 0,
      fakeDiscountMessage: data.fake_discount_message || "",
      strategy: item.strategy,
      matchedTerms: item.matched_terms || [],
      url,
      constructorData: data,
      variations,
    };
  };

  class ConstructorRecommendations {
    constructor() {
      this.client = null;
      this.isInitialized = false;
      this.currentRequests = new Map();
      this.retryCount = 0;
      this.maxRetries = 50;
      this.cache = new Map();
      this.cacheTTL = 5 * 60 * 1000;

      this.initializeWhenReady();
    }

    buildSizedImageUrl(src, width, height) {
      if (!src) return "";

      if (src.includes("width=") || src.includes("height=") || src.includes("_x")) {
        return src;
      }

      const separator = src.includes("?") ? "&" : "?";
      return `${src}${separator}width=${width}&height=${height}&crop=center`;
    }

    /**
     * Transform Constructor.io URLs to match current market domain and locale
     * @param {string} constructorUrl - Original URL from Constructor.io
     * @returns {string} - Transformed URL for current market
     */
    transformUrlForMarket(constructorUrl) {
      if (!constructorUrl) return "";

      const currentHostname = window.location.hostname;
      const currentPathname = window.location.pathname;
      const currentMarket = this.getCurrentMarket();

      try {
        const url = new URL(constructorUrl);

        // Replace domain based on current hostname
        url.hostname = currentHostname;

        // Handle market-specific path prefixes
        if (currentMarket === "canada") {
          // Check if we're on French Canada site
          if (currentPathname.startsWith("/fr") || currentHostname.includes("fr.")) {
            // Add /fr prefix if not already present
            if (!url.pathname.startsWith("/fr")) {
              url.pathname = "/fr" + url.pathname;
            }
          }
          // For English Canada (.ca), no prefix needed
        } else if (currentMarket === "international" && currentHostname.includes(".com")) {
          // Keep as .com without prefix
        }

        return url.toString();
      } catch (error) {
        // If URL parsing fails, try simple string replacement
        console.warn("URL parsing failed, using fallback method:", error);

        let transformedUrl = constructorUrl;

        // Replace domain
        transformedUrl = transformedUrl.replace(/https?:\/\/[^\/]+/, `${window.location.protocol}//${currentHostname}`);

        // Add French prefix for French Canada
        if (currentMarket === "canada" && (currentPathname.startsWith("/fr") || currentHostname.includes("fr."))) {
          transformedUrl = transformedUrl.replace(/\/products\//, "/fr/products/");
        }

        return transformedUrl;
      }
    }

    /**
     * Get current market for URL transformation
     * @returns {string} - Current market identifier
     */
    getCurrentMarket() {
      const fakeDiscountSettings = window.theme?.fakeDiscount;

      if (!fakeDiscountSettings) {
        return this.getFallbackMarket();
      }

      const detectedCountry = fakeDiscountSettings.currentMarket?.country;
      if (detectedCountry && fakeDiscountSettings.marketMapping) {
        const market = fakeDiscountSettings.marketMapping[detectedCountry];
        if (market) {
          return market;
        }
      }

      if (typeof window.Shopify !== "undefined" && window.Shopify.country) {
        const country = window.Shopify.country;
        const market = fakeDiscountSettings.marketMapping?.[country];
        if (market) {
          return market;
        }
      }

      const urlMarket = this.detectMarketFromUrl();
      if (urlMarket) {
        return urlMarket;
      }

      return "international";
    }

    getFallbackMarket() {
      if (typeof window.Shopify !== "undefined" && window.Shopify.country) {
        const mapping = {
          US: "us",
          CA: "canada",
          AU: "australia",
          GB: "uk",
          DE: "germany",
        };
        return mapping[window.Shopify.country] || "international";
      }
      return "international";
    }

    detectMarketFromUrl() {
      const hostname = window.location.hostname;
      const mappings = {
        ".ca": "canada",
        ".com.au": "australia",
        ".co.uk": "uk",
        ".de": "germany",
      };

      for (const [domain, market] of Object.entries(mappings)) {
        if (hostname.includes(domain)) return market;
      }
      return null;
    }

    initializeWhenReady() {
      const checkConstructor = () => {
        this.retryCount++;

        if ((window.ConstructorioClient || window.constructorio) && window.cnstrc && window.cnstrc.indexKey) {
          try {
            this.initializeConstructor();
            this.isInitialized = true;
          } catch (error) {}
        } else if (this.retryCount >= this.maxRetries) {
        } else {
          setTimeout(checkConstructor, 50);
        }
      };

      checkConstructor();
    }

    getProductId() {
      return window.productId || null;
    }

    initializeConstructor() {
      if (window.constructorManager) {
        this.client = window.constructorManager.getClient();
        if (!this.client) {
          console.warn("⚠️ Constructor.io client not available in recommendations");
          return;
        }
      } else if (window.constructorio) {
        this.client = window.constructorio;
      } else if (window.constructorioClient) {
        this.client = window.constructorioClient;
      } else if (window.constructorSearch && window.constructorSearch.client) {
        this.client = window.constructorSearch.client;
      } else if (window.constructorBrowse && window.constructorBrowse.client) {
        this.client = window.constructorBrowse.client;
      } else {
        console.warn("⚠️ Constructor.io not initialized for recommendations");
        return;
      }

      this.initializeRecommendationSections();
    }

    initializeRecommendationSections() {
      const sections = document.querySelectorAll("[data-constructor-recommendations]");
      sections.forEach((section) => {
        const podId = section.dataset.constructorRecommendations;
        const numResults = parseInt(section.dataset.numResults) || 8;

        if (podId) {
          this.loadRecommendations(podId, {
            section: section,
            numResults: numResults,
          });
        }
      });
    }

    async loadRecommendations(podId, options = {}) {
      if (!this.isInitialized || !this.client) {
        setTimeout(() => {
          if (this.isInitialized && this.client) {
            this.loadRecommendations(podId, options);
          }
        }, 500);
        return false;
      }

      const currentProductId = this.getProductId();
      const cacheKey = `${podId}-${options.numResults || 8}-${currentProductId || "no-item"}`;
      const cached = this.getCachedResult(cacheKey);
      if (cached) {
        this.renderRecommendations(cached.data, { ...options, podId });
        return true;
      }

      // Cancel only the request for THIS specific pod
      if (this.currentRequests.has(podId)) {
        this.currentRequests.get(podId).abort();
        this.currentRequests.delete(podId);
      }

      try {
        this.showLoadingState(options.section);

        const controller = new AbortController();
        this.currentRequests.set(podId, controller);

        const requestParams = {
          numResults: options.numResults || 8,
          section: "Products",
          fmt_options: {
            show_hidden_facets: false,
            show_hidden_fields: false,
            fields: [
              "data.handle",
              "data.title",
              "data.price",
              "data.compareAtPrice",
              "data.image_url",
              "data.inventory_quantity",
              "data.group_ids",
              "data.preorder_tag",
              "data.waterproof_tag",
              "data.bestseller_tag",
              "data.new_tag",
              "data.mediaImages",
              "data.url",
              "data.tags",
              "data.vendor",
              "data.product_type",
              "data.options",
              "data.fake_discount_percentage",
              "data.fake_discount_message",
              "data.created_at",
              "data.updated_at",
              "data.published_at",
              "data.created_date",
              "data.id",
              "variations",
            ],
          },
          ...(currentProductId && { itemIds: [currentProductId] }),
          ...options.filters,
        };

        let response;
        try {
          response = await this.client.recommendations.getRecommendations(podId, requestParams, {
            timeout: 5000,
            signal: controller.signal,
          });
        } catch (apiError) {
          throw apiError;
        }

        if (controller.signal.aborted) {
          return false;
        }

        this.currentRequests.delete(podId);

        const results = response.response?.results || response.results || [];
        const resultId = response.result_id || response.response?.result_id;

        if (results.length > 0) {
        }

        this.setCachedResult(cacheKey, {
          data: results,
          response: response,
          timestamp: Date.now(),
        });

        await this.renderRecommendations(results, {
          ...options,
          podId,
          resultId: resultId,
          totalResults: response.response?.total_num_results || results.length,
        });

        return true;
      } catch (error) {
        this.currentRequests.delete(podId);
        this.hideLoadingState(options.section);
        this.showErrorState(options.section);
        return false;
      }
    }

    async renderRecommendations(results, options = {}) {
      if (!results || results.length === 0) {
        this.showNoResults(options.section, options.podId);
        return;
      }

      try {
        const constructorProducts = results
          .map((item, index) => {
            let handle = null;

            if (item.data?.url) {
              const urlParts = item.data.url.split("/products/");
              if (urlParts.length > 1) {
                handle = urlParts[1].split("?")[0].split("#")[0];
              }
            }

            if (!handle) {
              handle =
                item.data?.handle ||
                item.data?.slug ||
                item.data?.shopify_handle ||
                item.value ||
                (item.data?.id && typeof item.data.id === "string" && /[a-zA-Z-]/.test(item.data.id) ? item.data.id : null);
            }

            return {
              handle: handle,
              constructorData: item.data,
              strategy: item.strategy,
              variations: item.variations || [],
              value: item.value,
              matchedTerms: item.matched_terms || [],
              isSlotted: item.is_slotted || false,
            };
          })
          .filter((item) => item.handle);

        const productHandles = constructorProducts.map((item) => item.handle);

        if (productHandles.length === 0) {
          this.showNoResults(options.section, options.podId);
          return;
        }

        const combinedProducts = [];
        for (const constructorProduct of constructorProducts) {
          try {
            const data = constructorProduct.constructorData || {};
            const variations = constructorProduct.variations || [];

            const productTitle = constructorProduct.value || data.title || "Product";

            const product = {
              handle: constructorProduct.handle,
              title: productTitle,
              price: Math.round((data.price || 0) * 100),
              compare_at_price: Math.round((data.compareAtPrice || 0) * 100),
              featured_image: {
                alt: productTitle,
                src: data.image_url || "",
              },
              images: (() => {
                const rawMedia = data.mediaImages;
                // Temporary logging to debug mediaImages type issues
                if (rawMedia && !Array.isArray(rawMedia)) {
                  console.log("mediaImages type:", typeof rawMedia, "value:", rawMedia, "for product:", productTitle);
                }
                const mediaArray = Array.isArray(rawMedia) ? rawMedia : rawMedia ? [rawMedia] : [];
                return mediaArray.map((img) => ({
                  alt: img?.altText || productTitle,
                  src: img?.transformedSrc || img?.src || "",
                }));
              })(),
              available: (data.inventory_quantity || 0) > 0,
              vendor: data.vendor || "",
              product_type: data.product_type || "",
              collections: [],
              has_only_default_variant: variations.length <= 1,
              options: (() => {
                const optionsMap = new Map();
                variations.forEach((variation) => {
                  const varData = variation.data || {};
                  if (varData.color) {
                    if (!optionsMap.has("Color")) {
                      optionsMap.set("Color", { name: "Color", values: new Set() });
                    }
                    optionsMap.get("Color").values.add(varData.color);
                  }
                });
                return Array.from(optionsMap.entries()).map(([name, option]) => ({
                  name,
                  values: Array.from(option.values),
                }));
              })(),
              variants:
                variations?.map((v) => {
                  const varData = v.data || {};
                  const variantTitle = varData.title || v.value || "Variant";

                  return {
                    id: varData.variation_id || v.id,
                    title: variantTitle,
                    price: Math.round((varData.price || data.price || 0) * 100),
                    compare_at_price: Math.round((varData.compareAtPrice || data.compareAtPrice || 0) * 100),
                    inventory_quantity: varData.inventory_quantity || 0,
                    available: (varData.inventory_quantity || 0) > 0,
                    image_url: varData.image_url,
                    color: varData.color,
                    size: varData.size,
                    discountPercentage: varData.discountPercentage || 0,
                  };
                }) || [],

              constructorData: constructorProduct.constructorData || constructorProduct,
              variations: variations,
              matchedTerms: constructorProduct.matched_terms || [],
              isSlotted: constructorProduct.is_slotted || false,
              url: this.transformUrlForMarket(data.url || `/products/${constructorProduct.handle}`),

              tags: [
                ...(data.group_ids || []),
                ...(data.preorder_tag ? ["preorder"] : []),
                ...(data.waterproof_tag ? ["waterproof"] : []),
                ...(data.bestseller_tag ? ["bestseller", "bestsellers"] : []),
                ...(data.new_tag ? ["new"] : []),
                ...(data.low_inventory_tag ? ["low-inventory"] : []),
              ],

              isPreorder: data.preorder_tag || false,
              isWaterproof: data.waterproof_tag || false,
              isBestseller: data.bestseller_tag || false,
              isNew: data.new_tag || false,
              isLowInventory: data.low_inventory_tag || false,

              fakeDiscountPercentage: data.fake_discount_percentage || 0,
              fakeDiscountMessage: data.fake_discount_message || "",
              data: constructorProduct,
              strategy: constructorProduct.strategy,
            };

            combinedProducts.push(product);
          } catch (error) {
            console.warn("Skipping bad product item:", error, constructorProduct);
          }
        }

        const productHTML = this.renderProductsTemplate(combinedProducts, options);

        const container = options.section || document.querySelector(`[data-constructor-recommendations="${options.podId}"]`);

        if (!container) {
          return;
        }

        if (!container.closest(".featured-products")) {
          const parentSection = container.closest("section") || container.parentElement;
          if (parentSection && !parentSection.classList.contains("featured-products")) {
            parentSection.classList.add("featured-products");
          }
        }

        const productList = container.querySelector(".product-list");
        if (productList) {
          productList.innerHTML = productHTML;
          productList.classList.add("constructor-loaded");

          productList.setAttribute("data-cnstrc-recommendations", "");
          productList.setAttribute("data-cnstrc-recommendations-pod-id", options.podId || "");
          productList.setAttribute("data-cnstrc-result-id", options.resultId || "");

          document.dispatchEvent(
            new CustomEvent("constructor:recommendations:loaded", {
              detail: {
                numResults: combinedProducts.length,
                podId: options.podId,
                section: options.sectionId || container.id,
              },
            })
          );
          productList.setAttribute("data-cnstrc-num-results", combinedProducts.length.toString());
        }

        this.initializeProductTracking(container, options.podId, combinedProducts);
        this.reinitializeComponents(container);

        this.initializeSplideCarousel(container);
        this.injectConstructorCSS();

        setTimeout(() => {
          if (typeof initFeaturedProducts === "function") {
            initFeaturedProducts();
          }
        }, 200);

        this.hideLoadingState(options.section);
      } catch (error) {
        this.hideLoadingState(options.section);
        this.showErrorState(options.section);
      }
    }

    renderProductsTemplate(products, options = {}) {
      if (!products || products.length === 0) {
        return '<li class="col-12"><p class="text-center">No products found.</p></li>';
      }

      return products
        .map((product, index) => {
          return this.renderSingleProductTemplate(product, { ...options, index });
        })
        .join("");
    }

    renderSingleProductTemplate(product, options = {}) {
      if (!product) return "";

      const productTitle = product.title || "";
      const productHandle = product.handle || "";

      // Transform Constructor.io URL for current market
      let productUrl = product.url || `/products/${productHandle}`;
      productUrl = this.transformUrlForMarket(productUrl);
      const firstVariant = (product.variants && product.variants[0]) || {};
      const isAvailable = product.available || false;

      const imgOrientation = window.theme?.productCard?.imgOrientation || "square";
      let imgWidth, imgHeight;

      switch (imgOrientation) {
        case "square":
          imgWidth = 460;
          imgHeight = 460;
          break;
        case "landscape":
          imgWidth = 200;
          imgHeight = 160;
          break;
        case "portrait":
          imgWidth = 160;
          imgHeight = 200;
          break;
        default:
          imgWidth = 460;
          imgHeight = 460;
      }

      const data = product.constructorData || {};

      const mediaImages = (() => {
        if (Array.isArray(product.images) && product.images.length > 0) {
          return product.images;
        }
        const rawMedia = data.mediaImages;
        if (Array.isArray(rawMedia)) {
          return rawMedia;
        }
        return rawMedia ? [rawMedia] : [];
      })();

      const primarySrcRaw =
        mediaImages[0]?.transformedSrc || mediaImages[0]?.src || product.featured_image?.src || data.image_url || "";
      const secondarySrcRaw = mediaImages[1]?.transformedSrc || mediaImages[1]?.src || null;

      const primarySrc = primarySrcRaw ? this.buildSizedImageUrl(primarySrcRaw, imgWidth, imgHeight) : "";
      const secondarySrc = secondarySrcRaw ? this.buildSizedImageUrl(secondarySrcRaw, imgWidth, imgHeight) : null;
      const imageUrl = primarySrc;
      const imageAlt = mediaImages[0]?.altText || product.featured_image?.alt || productTitle;

      const fakeDiscountResult = this.getSmartFakeDiscount(product);
      let finalPrice, originalPrice, isOnSale, discountPercentage, discountMessage;

      if (fakeDiscountResult.shouldApply) {
        finalPrice = fakeDiscountResult.finalPrice;
        originalPrice = fakeDiscountResult.originalPrice;
        isOnSale = true;
        discountPercentage = fakeDiscountResult.discountPercentage;
        discountMessage = fakeDiscountResult.message;
      } else {
        finalPrice = firstVariant.price || 0;
        originalPrice = firstVariant.compare_at_price || 0;
        isOnSale = originalPrice && originalPrice > finalPrice;
        discountPercentage = isOnSale ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100) : 0;
        discountMessage = null;
      }

      const primaryBadgeResult = this.getSmartPrimaryBadge(product, isOnSale, discountPercentage > 0);
      const showSaleBadge = window.theme?.productCard?.showSaleBadge && isOnSale;
      const saleBadgeColor = window.theme?.productCard?.saleBadgeColor || "primary";

      const swatchesResult = this.getSmartVariantSwatches(product, imgWidth, imgHeight);

      const formattedPrice = this.formatMoneyWithCurrency(finalPrice);
      const formattedComparePrice = isOnSale && originalPrice ? this.formatMoneyWithCurrency(originalPrice) : "";

      const cardStyle = window.theme?.productCard?.style;
      const spacingMobile = window.theme?.productCard?.spacing?.mobile ? `p-${window.theme.productCard.spacing.mobile}` : "p-3";
      const spacingDesktop = window.theme?.productCard?.spacing?.desktop
        ? `p-lg-${window.theme.productCard.spacing.desktop}`
        : "p-lg-4";
      const imgThumbnail = window.theme?.productCard?.imgThumbnail || "";

      const marginClass = cardStyle !== "card" ? "mb-4" : "";
      const strategyId = product.strategy?.id || "";

      const pngBackgroundClass =
        product.featured_image && (product.featured_image.alt || "").toLowerCase().includes("png") ? "png-background" : "";

      return `
      <li class="product-item splide__slide ${spacingMobile} ${spacingDesktop} ${marginClass}" 
          data-product-handle="${productHandle}"
          data-cnstrc-item="recommendation"
          data-cnstrc-item-id="${product.data?.id || product.id || productHandle}"
          data-cnstrc-item-name="${productTitle}"
          data-cnstrc-item-section="Products"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice / 100}"
          data-cnstrc-strategy-id="${strategyId}"
          data-cnstrc-position="${options.index + 1}">
        <div class="${cardStyle || ""}">
          <div class="img-wrapper position-relative mb-4${secondarySrc ? " has-hover-image" : ""}">
            <a href="${productUrl}">
              ${primaryBadgeResult.shouldShow ? primaryBadgeResult.badgeHTML : ""}
              
              ${
                showSaleBadge && discountPercentage > 0
                  ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute" style="z-index: 2;">
                       ${window.theme?.product?.save || "Save"} ${discountPercentage}%
                     </span>`
                  : ""
              }
              
              ${
                imageUrl
                  ? `
                <img class="product-item-img img-fluid rounded ${imgThumbnail} ${pngBackgroundClass}" 
                     src="${imageUrl}" 
                     alt="${imageAlt}" 
                     width="${imgWidth}" 
                     height="${imgHeight}" 
                     loading="lazy"
                     decoding="async"
                     style="width: 100%; height: auto; aspect-ratio: 1/1; object-fit: cover;">
                ${
                  secondarySrc
                    ? `
                <img 
                  src="${secondarySrc}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--secondary img-fluid rounded ${imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="eager"
                  decoding="async"
                  style="width: 100%; height: auto; aspect-ratio: 1/1; object-fit: cover;"
                />
                    `
                    : ""
                }
              `
                  : `
                <img class="product-item-img img-fluid rounded ${imgThumbnail}" 
                     src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' alignment-baseline='middle' font-family='system-ui, sans-serif' fill='%236c757d'%3eNo Image%3c/text%3e%3c/svg%3e" 
                     alt="${imageAlt}" 
                     width="${imgWidth}" 
                     height="${imgHeight}" 
                     loading="lazy"
                     style="width: 100%; height: auto; aspect-ratio: 1/1; object-fit: cover;">
              `
              }
            </a>

            <div class="wishlist-mobile">
              <button class="btn-wishlist-add-remove btn" 
                      type="button" 
                      data-product-handle="${productHandle}" 
                      data-text-add="Add to wishlist" 
                      data-text-remove="Remove from wishlist" 
                      aria-label="Add to wishlist" 
                      onclick="addOrRemoveFromWishlist(this)" 
                      data-cnstrc-btn="add_to_wishlist">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </button>
            </div>

            ${this.getSmartAddToCartForm(product)}
          </div>

          <div class="firstline-container">
            <div class="${cardStyle === "card" ? "card-body" : ""}">
              <h3 class="product-item-title mb-2 fs-1 fs-lg-1 text-truncate">
                <a class="product-link position-relative d-block link-dark" href="${productUrl}" tabindex="-1">
                  ${productTitle}
                </a>
              </h3>
              
              <!-- Review widgets matching featured-products exactly -->
              <div class="shopify-product-reviews-badge" data-id="${product.id}"></div>
              <div class="loox-rating" data-id="${product.id}" data-rating="" data-raters=""></div>
              <div style="" class="jdgm-widget jdgm-preview-badge" data-id="${product.id}"></div>
              <div data-oke-star-rating="" data-oke-reviews-product-id="shopify-${product.id}" hidden=""></div>
              <div class="wc_product_review_badge" data-handle="${productHandle}" data-product_id="${product.id}"></div>
              
              <p class="product-item-price product-item-price-carrossel mb-2" data-product-item-price="">
                ${
                  isOnSale
                    ? `
                      <span class="product-item-price-compare text-muted me-1">
                        <span class="visually-hidden">Regular price&nbsp;</span>
                        <s>${formattedComparePrice}</s>
                      </span>
                      <span class="product-item-price-final">
                        <span class="visually-hidden">Sale price&nbsp;</span>
                        ${formattedPrice}
                      </span>
                    `
                    : `
                      <span class="product-item-price-final">
                        ${formattedPrice}
                      </span>
                    `
                }
              </p>

              ${
                discountMessage
                  ? `
                <div class="fake-discount-message mt-2 p-2 rounded-2" 
                     style="color: ${
                       fakeDiscountResult.messageColor || window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000"
                     }; font-size: 0.875rem; line-height: 1.2;">
                  ${discountMessage}
                </div>
              `
                  : ""
              }

              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>

            <div class="product-promo-text-container"></div>
          </div>
           
          <div class="btn-atc-containerMobile" id="mobile-atc">
            ${this.getSmartAddToCartFormMobile(product)}
          </div>
        </div>
      </li>
    `;
    }

    getSmartFakeDiscount(product) {
      if (product.fakeDiscountPercentage && product.fakeDiscountPercentage > 0) {
        const basePrice = product.price || 0;
        const discountAmount = (basePrice * product.fakeDiscountPercentage) / 100;
        const discountedPrice = Math.ceil(basePrice - discountAmount);

        const themeMessage = window.theme?.fakeDiscount?.marketSettings?.message || "";
        const finalMessage = product.fakeDiscountMessage || themeMessage || "";

        return {
          shouldApply: true,
          finalPrice: discountedPrice,
          originalPrice: product.compare_at_price || product.price || 0,
          message: finalMessage,
          messageColor: window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000",
          discountPercentage: product.fakeDiscountPercentage,
          discountType: "percentage",
        };
      }

      const fakeDiscountSettings = window.theme?.fakeDiscount;

      if (!fakeDiscountSettings?.marketSettings?.enabled) {
        return {
          shouldApply: false,
          finalPrice: product.price || 0,
          originalPrice: product.compare_at_price || 0,
          message: null,
        };
      }

      const settings = fakeDiscountSettings.marketSettings;

      const hasDiscountAllowedTag =
        this.productHasTag(product, "discount allowed") || this.productHasTag(product, "discount-allowed");
      const hasConfiguredTag = this.productHasTag(product, settings.tag);
      const hasHighDiscount =
        product.compare_at_price &&
        product.price &&
        ((product.compare_at_price - product.price) / product.compare_at_price) * 100 >= 50;

      if (!hasDiscountAllowedTag && !hasConfiguredTag && !hasHighDiscount) {
        return {
          shouldApply: false,
          finalPrice: product.price || 0,
          originalPrice: product.compare_at_price || 0,
          message: null,
        };
      }

      let basePrice = 0;
      const priceSource = fakeDiscountSettings.priceSource || "price";

      if (priceSource === "price") {
        basePrice = product.price || 0;
      } else {
        if (product.compare_at_price && product.compare_at_price > product.price) {
          basePrice = product.compare_at_price;
        } else {
          basePrice = product.price || 0;
        }
      }

      let discountedPrice = 0;
      let actualPercentageForMessage = settings.percentage;

      if (settings.type === "fixed") {
        discountedPrice = parseFloat(settings.fixedAmount || 0) * 100;
        if (basePrice > 0) {
          actualPercentageForMessage = Math.round(((basePrice - discountedPrice) * 100) / basePrice);
        }
      } else {
        const discountAmount = (basePrice * settings.percentage) / 100;
        discountedPrice = basePrice - discountAmount;
        actualPercentageForMessage = settings.percentage;
      }

      let message = settings.message || "";
      message = message.replace(/\[percentage\]/g, settings.percentage);
      message = message.replace(/\[percentage-amount\]/g, actualPercentageForMessage);
      message = message.replace(/\[amount\]/g, settings.fixedAmount || "0");
      message = message.replace(/\[code\]/g, settings.code || "");

      const displayOriginalPrice = product.compare_at_price || product.price || 0;
      const roundedFinalPrice = Math.ceil(discountedPrice);

      return {
        shouldApply: true,
        finalPrice: roundedFinalPrice,
        originalPrice: displayOriginalPrice,
        message: message,
        messageColor: settings.messageColor,
        discountPercentage: actualPercentageForMessage,
        discountType: settings.type || "percentage",
        fixedAmount: settings.fixedAmount,
        basePrice: basePrice,
      };
    }

    productHasTag(product, requiredTag) {
      if (!product.tags || !requiredTag) return false;
      const tags = Array.isArray(product.tags) ? product.tags : (product.tags || "").split(",").map((tag) => tag.trim());
      return tags.includes(requiredTag);
    }

    getSmartPrimaryBadge(product, isOnSale, hasDiscount) {
      if (this.productHasTag(product, "preorder") || this.productHasTag(product, "pre-order")) {
        return { shouldShow: false, badgeHTML: null, badgeType: "preorder-skip" };
      }

      const promoBadgeSettings = window.theme?.promoBadge;
      if (promoBadgeSettings?.enabled && hasDiscount) {
        return this.getSmartPromoBadge(product);
      }

      const customBadgeSettings = window.theme?.customBadge;
      if (customBadgeSettings?.globalEnabled && customBadgeSettings?.marketSettings?.enabled) {
        return this.getSmartCustomBadge(product);
      }

      const commercialBadgeSettings = window.theme?.commercialBadge;
      if (commercialBadgeSettings?.enabled) {
        return this.getSmartCommercialBadge(product);
      }

      return { shouldShow: false, badgeHTML: null, badgeType: "none" };
    }

    getSmartCommercialBadge(product) {
      const commercialBadgeSettings = window.theme?.commercialBadge;

      if (!commercialBadgeSettings?.enabled) {
        return {
          shouldShow: false,
          badgeHTML: null,
          badgeType: "commercial-disabled",
        };
      }

      let badgeType = null;
      let badgeSettings = null;

      if (this.productHasTag(product, "waterproof")) {
        badgeType = "waterproof";
        badgeSettings = commercialBadgeSettings.waterproof;
      } else if (this.productHasTag(product, "new")) {
        badgeType = "new";
        badgeSettings = commercialBadgeSettings.new;
      } else if (this.productHasTag(product, "bestsellers")) {
        badgeType = "bestseller";
        badgeSettings = commercialBadgeSettings.bestseller;
      } else if (this.productHasTag(product, "low-inventory")) {
        badgeType = "lowInventory";
        badgeSettings = commercialBadgeSettings.lowInventory;
      }

      if (!badgeType || !badgeSettings?.text) {
        return {
          shouldShow: false,
          badgeHTML: null,
          badgeType: "commercial-no-match",
        };
      }

      const badgeHTML = `
      <div class="commercial-badge-wrapper position-absolute" style="z-index: 2;">
        <span class="commercial-badge ${badgeType}-badge"
              style="background-color: ${badgeSettings.bgColor}; 
                     color: ${badgeSettings.textColor}; 
                     font-size: ${commercialBadgeSettings.fontSize}px; 
                     border-radius: ${commercialBadgeSettings.borderRadius}px; 
                     opacity: ${commercialBadgeSettings.opacity / 100}; 
                     padding: ${commercialBadgeSettings.padding}px; 
                     width: ${commercialBadgeSettings.width}%; 
                     height: ${commercialBadgeSettings.height}%; 
                     display: inline-flex; 
                     align-items: center; 
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;
                     margin: 10px;">
          ${badgeSettings.text}
        </span>
      </div>
    `;

      return {
        shouldShow: true,
        badgeHTML: badgeHTML,
        badgeType: `commercial-${badgeType}`,
      };
    }

    getSmartPromoBadge(product) {
      const promoBadgeSettings = window.theme?.promoBadge;

      if (!promoBadgeSettings?.enabled) {
        return {
          shouldShow: false,
          badgeHTML: null,
          badgeType: "promo-disabled",
        };
      }

      // Get first variant for price comparison (like Liquid)
      const variant = product.variants?.[0] || product;
      const compareAtPrice = variant.compare_at_price || product.compare_at_price || 0;
      const price = variant.price || product.price || 0;

      // Check if there's actually a discount
      if (!compareAtPrice || compareAtPrice <= price) {
        return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
      }

      // Calculate savings percentage (replicate Liquid logic)
      const savingsPercentage = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);

      // Round to nearest 5 (like Liquid logic)
      const remainder = savingsPercentage % 5;
      const roundedPercentage = remainder > 2 ? savingsPercentage + 5 - remainder : savingsPercentage - remainder;

      // Calculate opacity
      const opacity = (promoBadgeSettings.opacity || 100) / 100;
      const bgColor = promoBadgeSettings.bgColor || "#ff0000";

      // Extract RGB values for rgba
      const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
      const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
      const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
      const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

      const badgeHTML = `
        <div class="promo-badge-wrapper position-absolute" style="z-index: 1;">
          <span class="promo-badge badge"
                style="padding: ${promoBadgeSettings.padding || 8}px;
                       width: ${promoBadgeSettings.width || 60}px;
                       height: ${promoBadgeSettings.height || 60}px;
                       font-size: ${promoBadgeSettings.fontSize || 12}px;
                       background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                       color: ${promoBadgeSettings.textColor || "#ffffff"};
                       border-radius: ${promoBadgeSettings.borderRadius || 50}px;
                       display: inline-flex;
                       align-items: center;
                       justify-content: center;
                       font-weight: 500;
                       text-transform: uppercase;
                       line-height: 1.2;">
            ${roundedPercentage}% OFF
          </span>
        </div>
      `;

      return {
        shouldShow: true,
        badgeHTML: badgeHTML,
        badgeType: "promo",
        discountPercentage: roundedPercentage,
      };
    }

    getSmartCustomBadge(product) {
      const customBadgeSettings = window.theme?.customBadge;

      if (!customBadgeSettings?.globalEnabled || !customBadgeSettings?.marketSettings?.enabled) {
        return { shouldShow: false, badgeHTML: null };
      }

      const settings = customBadgeSettings.marketSettings;

      const badgeHTML = `
      <div class="custom-badge-wrapper position-absolute" style="z-index: 2;">
        <span class="custom-badge badge"  
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px; 
                     display: flex; 
                     align-items: center; 
                     justify-content: center;">
          ${settings.text}
        </span>
      </div>
    `;

      return {
        shouldShow: true,
        badgeHTML: badgeHTML,
      };
    }

    getSmartVariantSwatches(product, imgWidth, imgHeight) {
      const swatchSettings = window.theme?.variantSwatches;

      if (!swatchSettings?.enabled) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const variations = product?.variations || product?.variants || [];

      if (!variations || variations.length === 0) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const colorSet = new Set();
      variations.forEach((variation) => {
        const varData = variation.data || variation;

        let color = varData.color || varData.colour;

        if (!color && varData.title) {
          const titleParts = varData.title.split(" / ");
          if (titleParts.length >= 2) {
            color = titleParts[1];
          } else {
            color = varData.title;
          }
        }

        if (color && color.trim()) {
          colorSet.add(color.trim());
        }
      });

      const swatchValues = Array.from(colorSet);

      if (swatchValues.length === 0) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const maxVisible = swatchSettings.maxVisible || 4;

      let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" aria-label="Color options">
    `;

      let visibleSwatchCount = 0;

      swatchValues.forEach((colorValue, index) => {
        let variantFound = null;

        for (const variation of variations) {
          const varData = variation.data || variation;
          const varColor = varData.color || varData.colour;
          const varTitle = varData.title || "";

          const colorSearch = colorValue.toLowerCase();

          if (varColor && varColor.toLowerCase() === colorSearch) {
            variantFound = varData;
            break;
          }

          if (varTitle) {
            const variantTitle = varTitle.toLowerCase();
            if (
              variantTitle.includes(colorSearch) ||
              variantTitle.endsWith(`/ ${colorSearch}`) ||
              variantTitle.startsWith(`${colorSearch} /`)
            ) {
              variantFound = varData;
              break;
            }
          }
        }

        const showSoldOut = swatchSettings.showSoldOut;

        let isVariantAvailable = true;
        if (variantFound) {
          if (variantFound.hasOwnProperty("available")) {
            isVariantAvailable = variantFound.available;
          } else if (variantFound.hasOwnProperty("inventory_quantity")) {
            isVariantAvailable = variantFound.inventory_quantity > 0;
          }
        }

        if (!showSoldOut && variantFound && !isVariantAvailable) {
          return;
        }

        const isHidden = visibleSwatchCount >= maxVisible ? "hidden" : "";
        visibleSwatchCount++;

        let variantImageUrl = product.data?.image_url || product.image_url || product.featured_image?.src || "";

        if (variantFound?.image_url) {
          variantImageUrl = variantFound.image_url;
        } else if (variantFound?.featured_image?.src) {
          variantImageUrl = variantFound.featured_image.src;
        } else if (variantFound?.image?.src) {
          variantImageUrl = variantFound.image.src;
        }

        if (variantImageUrl) {
          const targetW = imgWidth || 460;
          const targetH = imgHeight || 460;
          variantImageUrl = this.buildSizedImageUrl(variantImageUrl, targetW, targetH);
        }

        swatchesHTML += `
        <li class="p-1" ${isHidden}>
          <button class="btn${!isVariantAvailable ? " swatch-unavailable" : ""}" type="button"
                  data-img="${variantImageUrl}"
                  data-img-width="${imgWidth || 460}"
                  data-img-height="${imgHeight || 460}"
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${isVariantAvailable}"
                  data-color-value="${colorValue}"
                  onclick="onChangeConstructorProductSwatches(this, event)">
      `;

        if (swatchSettings.useVariantImages && variantImageUrl) {
          swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${variantImageUrl}"
               alt="${colorValue}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
        } else {
          const swatchImageName = colorValue.toLowerCase().replace(/[\/\s]+/g, "-");
          const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

          swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${colorValue}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
        }

        swatchesHTML += `
          </button>
        </li>
      `;
      });

      const showMoreButton = visibleSwatchCount > maxVisible;
      if (showMoreButton) {
        const hiddenCount = visibleSwatchCount - maxVisible;
        swatchesHTML += `
        <li class="p-0">
          <button class="btn btn-view-more" type="button"
                  onclick="showAllConstructorSwatchesInProduct(this, event)"
                  aria-label="+${hiddenCount} more color options">
            +${hiddenCount}
          </button>
        </li>
      `;
      }

      swatchesHTML += `
        </ul>

        <div class="wishlistDesktop">
          <button class="btn-wishlist-add-remove btn" 
                  type="button" 
                  data-product-handle="${product.handle}" 
                  data-text-add="Add to wishlist" 
                  data-text-remove="Remove from wishlist" 
                  aria-label="Add to wishlist" 
                  onclick="addOrRemoveFromWishlist(this)" 
                  data-cnstrc-btn="add_to_wishlist">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

      return {
        shouldShow: visibleSwatchCount > 0,
        swatchesHTML: swatchesHTML,
        colorCount: visibleSwatchCount,
      };
    }

    getSmartAddToCartForm(product) {
      const settings = window.theme?.productCard;

      if (!settings?.showAtcForm) {
        return "";
      }

      const isCustomizedProduct =
        product.collections?.some((collection) => collection.handle === "customized") ||
        product.tags?.some((tag) => tag === "customized");

      if (isCustomizedProduct) {
        return this.generateViewProductButton(product);
      }

      if (!product || !product.variants || product.variants.length === 0) {
        return "";
      }

      const availableVariants = product.variants.filter((variant) => variant.available);

      if (!product.available || availableVariants.length === 0) {
        return this.generateSoldOutButton();
      }

      if (availableVariants.length === 0) {
        return this.generateSimpleAddToCartButton(product, null);
      } else {
        return this.generateDropdownAddToCartButton(product, availableVariants);
      }
    }

    getSmartAddToCartFormMobile(product) {
      const settings = window.theme?.productCard;

      if (!settings?.showAtcForm) {
        return "";
      }

      const isCustomizedProduct =
        product.collections?.some((collection) => collection.handle === "customized") ||
        product.tags?.some((tag) => tag === "customized");

      if (isCustomizedProduct) {
        return this.generateViewProductButtonMobile(product);
      }

      if (!product || !product.variants || product.variants.length === 0) {
        return "";
      }

      const availableVariants = product.variants.filter((variant) => variant.available);

      if (!product.available || availableVariants.length === 0) {
        return this.generateSoldOutButtonMobile();
      }

      if (availableVariants.length === 0) {
        return this.generateSimpleAddToCartButtonMobile(product, null);
      } else {
        return this.generateDropdownAddToCartButtonMobile(product, availableVariants);
      }
    }

    generateViewProductButton(product) {
      const translations = window.theme?.productCard?.translations;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <a href="${productUrl}" 
             class="view-product-btn-desktop"
             id="btn-vp-desktop"
             role="button">
            ${translations?.viewProduct || "View Product"}
          </a>
        </div>
      </div>
    `;
    }

    generateSoldOutButton() {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <button class="btn-atc btn w-100 btn-primary rounded-pill" id="btn-atc-desktop" disabled>
            ${translations?.soldOut || "Sold Out"}
          </button>
        </div>
      </div>
    `;
    }

    generateSimpleAddToCartButton(product, variant) {
      const translations = window.theme?.productCard?.translations;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="btn-atc btn btn-md w-100 btn-primary rounded-pill"
                    type="submit"
                    id="btn-atc-desktop"
                    name="add"
                    data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}">
              ${translations?.addToCart || "Add To Cart"}
            </button>
          </form>
        </div>
      </div>
    `;
    }

    generateDropdownAddToCartButton(product, availableVariants) {
      const translations = window.theme?.productCard?.translations;
      const showInventory = window.theme?.productCard?.formShowInventory;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      const dropdownItems = availableVariants
        .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations))
        .join("");

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form method="post" action="/cart/add" id="product_form_${product.id}" accept-charset="UTF-8" class="shopify-product-form product-item-form" enctype="multipart/form-data" onsubmit="onSubmitAtcForm(this, event)" data-product-id="${product.id}" data-product-title="${product.title}" data-product-url="${productUrl}">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="w-200 dropup">
              <button type="button" class="btn-atc btn btn-md dropdown-toggle btn-primary w-200 rounded-pill" id="btn-atc-desktop" data-bs-toggle="dropdown" data-text-add-to-cart="Add To Cart" aria-expanded="false">
                Add To Cart
              </button>
              <ul class="dropdown-menu shadow w-200">
                ${dropdownItems}
              </ul>
            </div>
            <input type="hidden" name="product-id" value="${product.id}">
          </form>
        </div>
      </div>
    `;
    }

    generateVariantDropdownItem(variant, showInventory, translations) {
      const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

      return `
      <li class="deny">
        <button class="dropdown-item" onclick="onClickAtcFormVariant(this, event)" data-variant-id="${
          variant.id
        }" type="button" data-cnstrc-btn="add_to_cart">
          <div class="d-flex flex-column justify-content-start lh-sm">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay || '<small class="text-success d-block">In stock</small>'}
          </div>
        </button>
      </li>
    `;
    }

    getInventoryDisplay(variant, showInventory, translations) {
      if (!showInventory) {
        return "";
      }

      if (variant.available) {
        if (!variant.inventory_quantity || variant.inventory_quantity <= 0 || variant.inventory_quantity >= 10) {
          return `<small class="text-success d-block">${translations?.inStock || "In stock"}</small>`;
        } else {
          const stockText = translations?.stockCount || "Only [count] left in stock";
          return `<small class="text-danger d-block">${stockText.replace("[count]", variant.inventory_quantity)}</small>`;
        }
      } else {
        return `<small class="text-muted d-block">${translations?.soldOut || "Sold out"}</small>`;
      }
    }

    generateViewProductButtonMobile(product) {
      const translations = window.theme?.productCard?.translations;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           id="new-view-product-btn-mobile"
           role="button">
          ${translations?.viewProduct || "View Product"}
        </a>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
    }

    generateSoldOutButtonMobile() {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary rounded-pill" disabled>
          ${translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
    }

    generateSimpleAddToCartButtonMobile(product, variant) {
      const translations = window.theme?.productCard?.translations;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${product.title}"
              data-product-url="${productUrl}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary rounded-pill"
                  type="submit"
                  name="add"
                  data-text-add-to-cart="${translations?.addToCart || "Add to cart"}">
            ${translations?.addToCart || "Add to cart"}
          </button>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
    }

    generateDropdownAddToCartButtonMobile(product, availableVariants) {
      const translations = window.theme?.productCard?.translations;
      const showInventory = window.theme?.productCard?.formShowInventory;
      let productUrl = product.url || `/products/${product.handle}`;
      productUrl = this.transformUrlForMarket(productUrl);

      const variantItems = availableVariants
        .map((variant) => this.generateVariantDropdownItemMobile(variant, showInventory, translations))
        .join("");

      return `
      <div class="mobile-view mb-2">
        <form method="post" action="/cart/add" id="product_form_${
          product.id
        }" accept-charset="UTF-8" class="shopify-product-form product-item-form" enctype="multipart/form-data" onsubmit="onSubmitAtcForm(this, event)" data-product-id="${
        product.id
      }" data-product-title="${product.title}" data-product-url="${productUrl}">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button" class="btn-atc w-100 rounded-pill" id="new-add-to-bag-btn-mobile" data-bs-toggle="dropdown" data-text-add-to-cart="Add To Cart" aria-expanded="false">
              Add To Cart
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
          <input type="hidden" name="product-id" value="${product.id}">
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
    }

    generateVariantDropdownItemMobile(variant, showInventory, translations) {
      const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

      return `
      <li class="deny">
        <button class="dropdown-item" onclick="onClickAtcFormVariant(this, event)" data-variant-id="${
          variant.id
        }" type="button" data-cnstrc-btn="add_to_cart">
          <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay ? `<small class="text-success">${inventoryDisplay.replace(/<[^>]*>/g, "")}</small>` : ""}
          </div>
        </button>
      </li>
    `;
    }

    formatMoneyWithCurrency(cents) {
      const roundedCents = Math.round(cents);
      const currencyCode = window.theme?.fakeDiscount?.currentMarket?.currency || "USD";

      try {
        const formatted = new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(roundedCents / 100);

        return formatted;
      } catch (error) {
        return this.formatMoney(roundedCents);
      }
    }

    formatMoney(cents, format) {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }

      cents = Math.round(cents);

      let value = "";
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || window.Shopify?.money_format || "${{amount}}";

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = precision || 2;
        thousands = thousands || ",";
        decimal = decimal || ".";

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split(".");
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsAmount = parts[1] ? decimal + parts[1] : "";

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

    getCachedResult(key) {
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
        return cached;
      }
      this.cache.delete(key);
      return null;
    }

    setCachedResult(key, data) {
      this.cache.set(key, data);
    }

    showLoadingState(section) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.classList.add("constructor-loading");
        if (productList.children.length === 0) {
          const skeletonCount = Number(section.dataset.numResults) || 8;
          const frag = document.createDocumentFragment();
          for (let i = 0; i < skeletonCount; i++) {
            const li = document.createElement("li");
            li.className = "product-item splide__slide p-2 p-lg-3 mb-4 skeleton-card";
            li.innerHTML =
              '<div class="img-wrapper"><div class="skeleton skeleton-img"></div></div>' +
              '<div class="skeleton skeleton-line w-75 mx-auto mb-2"></div>' +
              '<div class="skeleton skeleton-line w-50 mx-auto"></div>';
            frag.appendChild(li);
          }
          productList.appendChild(frag);
        }
      }
    }

    hideLoadingState(section) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.classList.remove("constructor-loading");
        productList.querySelectorAll(".skeleton-card").forEach((el) => el.remove());
      }
    }

    showErrorState(section) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.innerHTML = `
        <div class="col-12">
          <div class="alert alert-warning text-center py-4" role="status">
            <h4>Unable to load recommendations</h4>
            <p class="mb-0">Please try again later.</p>
          </div>
        </div>
      `;
      }
    }

    showNoResults(section, podId) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.innerHTML = `
        <div class="col-12">
          <div class="alert alert-info text-center py-4" role="status">
            <h4>No recommendations available</h4>
            <p class="mb-0">Check back later for new products.</p>
          </div>
        </div>
      `;
      }
    }

    initializeProductTracking(container, podId, products) {
      const productItems = container.querySelectorAll(".product-item");
      productItems.forEach((item, index) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink && this.client?.tracker) {
          const itemId = item.getAttribute("data-cnstrc-item-id");
          const itemName = item.getAttribute("data-cnstrc-item-name");
          const position = item.getAttribute("data-cnstrc-position");
          const strategyId = item.getAttribute("data-cnstrc-strategy-id");

          productLink.removeEventListener("click", this.handleProductClick);

          let touchStartY = 0;
          let touchStartTime = 0;
          let isScrolling = false;

          const handleTouchStart = (event) => {
            touchStartY = event.touches[0].clientY;
            touchStartTime = Date.now();
            isScrolling = false;
          };

          const handleTouchMove = (event) => {
            const touchCurrentY = event.touches[0].clientY;
            const deltaY = Math.abs(touchCurrentY - touchStartY);

            if (deltaY > 10) {
              isScrolling = true;
            }
          };

          const handleClick = (event) => {
            const timeSinceTouch = Date.now() - touchStartTime;

            if (isScrolling || (timeSinceTouch < 150 && touchStartTime > 0)) {
              return;
            }

            try {
              if (this.client?.tracker) {
                this.client.tracker.trackClick({
                  item_id: itemId,
                  item_name: itemName,
                  item_section: "Products",
                  position: parseInt(position) || index + 1,
                  strategy_id: strategyId,
                  pod_id: podId,
                });
              }
            } catch (error) {
              console.warn("Constructor.io tracking error:", error);
            }
          };

          productLink.addEventListener("touchstart", handleTouchStart, { passive: true });
          productLink.addEventListener("touchmove", handleTouchMove, { passive: true });
          productLink.addEventListener("click", handleClick);

          productLink._constructorClickHandler = handleClick;
          productLink._constructorTouchStartHandler = handleTouchStart;
          productLink._constructorTouchMoveHandler = handleTouchMove;
        }
      });
    }

    cleanupProductTracking(container) {
      const productItems = container.querySelectorAll(".product-item");
      productItems.forEach((item) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink) {
          if (productLink._constructorClickHandler) {
            productLink.removeEventListener("click", productLink._constructorClickHandler);
            delete productLink._constructorClickHandler;
          }

          if (productLink._constructorTouchStartHandler) {
            productLink.removeEventListener("touchstart", productLink._constructorTouchStartHandler);
            delete productLink._constructorTouchStartHandler;
          }

          if (productLink._constructorTouchMoveHandler) {
            productLink.removeEventListener("touchmove", productLink._constructorTouchMoveHandler);
            delete productLink._constructorTouchMoveHandler;
          }
        }
      });
    }

    reinitializeComponents(container) {
      this.cleanupProductTracking(container);

      document.dispatchEvent(
        new CustomEvent("constructor:recommendations-loaded", {
          detail: { container },
        })
      );

      if (window.ProductCard) {
        container.querySelectorAll(".product-item").forEach((card) => {
          new ProductCard(card);
        });
      }
    }

    initializeSplideCarousel(container) {
      try {
        const existingSplides = container.querySelectorAll(".splide.is-initialized");
        existingSplides.forEach((element) => {
          element.classList.remove("is-initialized");

          if (element.splide) {
            try {
              element.splide.destroy();
            } catch (e) {}
            delete element.splide;
          }

          const arrows = element.querySelectorAll(".splide__arrow");
          const pagination = element.querySelectorAll(".splide__pagination");
          arrows.forEach((arrow) => arrow.remove());
          pagination.forEach((pag) => pag.remove());
        });

        const splideElements = container.querySelectorAll(".splide");

        if (splideElements.length === 0) {
          return;
        }

        if (typeof Splide === "undefined") {
          splideElements.forEach((splideEl) => {
            this.fallbackToGrid(splideEl);
          });
          return;
        }

        splideElements.forEach((element, index) => {
          const slidesCount = element.querySelectorAll(".splide__slide").length;
          const currentBreakpoint = window.innerWidth;
          let perPageCurrent = Number(element.dataset.breakpointXxl); // default

          if (currentBreakpoint < 576) perPageCurrent = Number(element.dataset.breakpointXs);
          else if (currentBreakpoint < 768) perPageCurrent = Number(element.dataset.breakpointSm);
          else if (currentBreakpoint < 992) perPageCurrent = Number(element.dataset.breakpointMd);
          else if (currentBreakpoint < 1200) perPageCurrent = Number(element.dataset.breakpointLg);
          else if (currentBreakpoint < 1400) perPageCurrent = Number(element.dataset.breakpointXl);

          const shouldHaveArrows = slidesCount > perPageCurrent;

          const isMobile = window.innerWidth <= 575;

          const mySplide = new Splide(element, {
            arrows: !isMobile && shouldHaveArrows,
            pagination: !isMobile && element.dataset.pagination === "true",
            easing: element.dataset.easing,
            speed: Number(element.dataset.speed),
            perMove: Number(element.dataset.perMove),
            autoplay: element.dataset.autoplay === "true",
            interval: Number(element.dataset.interval),
            per_move: Number(element.dataset.perMove),
            rewind: element.dataset.rewind === "true",
            mediaQuery: "min",
            breakpoints: {
              0: {
                perPage: Number(element.dataset.breakpointXs),
                padding: element.dataset.breakpointXsPartial === "true" ? { right: "16%" } : 0,
                arrows: false,
                pagination: false,
              },
              576: {
                perPage: Number(element.dataset.breakpointSm),
                padding: 0,
                arrows: shouldHaveArrows,
                pagination: element.dataset.pagination === "true",
              },
              768: { perPage: Number(element.dataset.breakpointMd), padding: 0 },
              992: { perPage: Number(element.dataset.breakpointLg), padding: 0 },
              1200: { perPage: Number(element.dataset.breakpointXl), padding: 0 },
              1400: {
                perPage: Number(element.dataset.breakpointXxl),
                padding: 0,
              },
            },
            direction: document.documentElement.getAttribute("dir"),
          });

          mySplide.on("mounted", () => {
            const slidesCount = element.querySelectorAll(".splide__slide").length;
            const arrowsCount = element.querySelectorAll(".splide__arrow").length;

            if (slidesCount > 1 && arrowsCount === 0) {
              try {
                mySplide.refresh();
              } catch (e) {}
            }

            setTimeout(() => {
              fixArrowsPos();
              fixPagination();
            }, 100);
          });

          mySplide.on("moved", () => {
            if (window.innerWidth <= 575) {
              return;
            }

            setTimeout(() => {
              const pagination = element.querySelector(".splide__pagination");
              if (pagination) {
                const activeDots = pagination.querySelectorAll(".is-active").length;
                if (activeDots !== 1) {
                  fixPagination();
                }
              }
            }, 50);
          });

          const fixArrowsPos = () => {
            let top = container.querySelector(".product-item-img")?.clientHeight / 2;

            if (container.querySelector(".product-item .card")) {
              top = container.querySelector(".card")?.clientHeight / 2;
            }

            container.querySelectorAll(".splide__arrow").forEach((arrow) => {
              arrow.style.top = `${top}px`;
            });
          };

          const fixPagination = () => {
            if (window.innerWidth <= 575) {
              return;
            }

            if (element.dataset.pagination === "true") {
              setTimeout(() => {
                const pagination = element.querySelector(".splide__pagination");
                if (pagination) {
                  const dots = pagination.querySelectorAll(".splide__pagination__page");
                  dots.forEach((dot, index) => {
                    if (index === 0) {
                      dot.classList.add("is-active");
                    } else {
                      dot.classList.remove("is-active");
                    }
                  });

                  if (pagination.children.length <= 1) {
                    pagination.style.display = "none";
                  } else {
                    pagination.style.display = "";
                  }
                }
              }, 100);
            }
          };

          mySplide.mount();

          element.splide = mySplide;

          element.classList.add("is-initialized");
        });
      } catch (error) {
        container.querySelectorAll(".splide").forEach((splideEl) => {
          this.fallbackToGrid(splideEl);
        });
      }
    }

    fallbackToGrid(splideElement) {
      const track = splideElement.querySelector(".splide__track");
      const list = splideElement.querySelector(".splide__list");

      if (track && list) {
        splideElement.classList.remove("splide");
        splideElement.classList.add("fallback-grid");

        track.classList.remove("splide__track");
        list.classList.remove("splide__list");
        list.classList.add("row", "g-3");

        const slides = list.querySelectorAll(".splide__slide");
        slides.forEach((slide) => {
          slide.classList.remove("splide__slide");
          slide.classList.add("col-6", "col-md-4", "col-lg-3");
        });
      }
    }

    injectConstructorCSS() {
      // Check if CSS is already injected
      if (document.getElementById("constructor-recommendations-styles")) {
        return;
      }

      const style = document.createElement("style");
      style.id = "constructor-recommendations-styles";
      style.textContent = `
      .product-list.constructor-loaded .product-item,
      [data-cnstrc-recommendations] .product-item {
        position: relative;
      }

      /* Ensure proper button positioning on Constructor products */
      .product-list.constructor-loaded .btn-atc-container,
      [data-cnstrc-recommendations] .btn-atc-container {
        position: absolute;
        bottom: 10px;
        right: 10px;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
      }

      .product-list.constructor-loaded .product-item:hover .btn-atc-container,
      [data-cnstrc-recommendations] .product-item:hover .btn-atc-container {
        opacity: 1;
        transform: translateY(0);
      }

      /* Button styling for Constructor products */
      .product-list.constructor-loaded #btn-atc-desktop,
      .product-list.constructor-loaded #btn-vp-desktop,
      .product-list.constructor-loaded .view-product-btn-desktop,
      [data-cnstrc-recommendations] #btn-atc-desktop,
      [data-cnstrc-recommendations] #btn-vp-desktop,
      [data-cnstrc-recommendations] .view-product-btn-desktop {
        position: relative;
        width: 100%;
        min-width: 105px;
        padding: 8px 16px;
        margin-top: 16px;
        font-size: 10px !important;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;
        background-color: #000000;
        color: #ffffff;
        border: none;
        border-radius: 24px !important;
        cursor: pointer;
        transition: all 0.3s ease-in-out;
        outline: none;
      }

      .product-list.constructor-loaded .btn-atc:hover,
      [data-cnstrc-recommendations] .btn-atc:hover {
        background-color: #444;
      }

      /* Price styling for Constructor products */
      .product-list.constructor-loaded .product-item-price-carrossel,
      [data-cnstrc-recommendations] .product-item-price-carrossel {
        font-family: 'Montserrat', sans-serif;
        font-weight: 500;
        text-align: center;
        font-size: 1rem;
        letter-spacing: 0.04em;
        line-height: 1.6;
      }

      /* Mobile responsive styles */
      @media (max-width: 768px) {
        .product-list.constructor-loaded .btn-atc-container,
        [data-cnstrc-recommendations] .btn-atc-container {
          display: none;
        }

        .product-list.constructor-loaded .btn-atc-containerMobile,
        [data-cnstrc-recommendations] .btn-atc-containerMobile {
          display: flex;
          flex-direction: column;
        }

        .product-list.constructor-loaded #new-add-to-bag-btn-mobile,
        .product-list.constructor-loaded #new-view-product-btn-mobile,
        [data-cnstrc-recommendations] #new-add-to-bag-btn-mobile,
        [data-cnstrc-recommendations] #new-view-product-btn-mobile {
          position: relative;
          width: 100%;
          min-width: 105px;
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-align: center;
          background-color: #000000;
          color: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.2);
          border-radius: 24px;
          cursor: pointer;
          transition: all 0.3s ease-in-out;
          outline: none;
        }

        .product-list.constructor-loaded .wishlist-mobile,
        [data-cnstrc-recommendations] .wishlist-mobile {
          display: flex;
        }

        .product-list.constructor-loaded .wishlistDesktop,
        [data-cnstrc-recommendations] .wishlistDesktop {
          display: none;
        }
      }

      /* Desktop styles */
      @media (min-width: 768px) {
        .product-list.constructor-loaded .mobile-view,
        [data-cnstrc-recommendations] .mobile-view {
          display: none;
        }

        .product-list.constructor-loaded .wishlist-mobile,
        [data-cnstrc-recommendations] .wishlist-mobile {
          display: none;
        }
      }


      /* Image hover effect - Secondary image styling */
      .product-list.constructor-loaded .img-wrapper .product-item-img--secondary,
      [data-cnstrc-recommendations] .img-wrapper .product-item-img--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
        z-index: 1;
      }

      .product-list.constructor-loaded .img-wrapper.has-hover-image:hover .product-item-img--secondary,
      [data-cnstrc-recommendations] .img-wrapper.has-hover-image:hover .product-item-img--secondary {
        opacity: 1;
      }

      .product-list.constructor-loaded .img-wrapper.has-hover-image:hover .product-item-img:not(.product-item-img--secondary),
      [data-cnstrc-recommendations] .img-wrapper.has-hover-image:hover .product-item-img:not(.product-item-img--secondary) {
        opacity: 0;
      }

      /* PNG background for product images */
      .product-list.constructor-loaded .png-background,
      [data-cnstrc-recommendations] .png-background {
        background-color:rgb(255, 255, 255);
        border-radius: 5px;
      }
    `;

      document.head.appendChild(style);
    }
  }

  let globalTouchState = {
    startY: 0,
    startTime: 0,
    isScrolling: false,
  };

  document.addEventListener(
    "touchstart",
    (e) => {
      globalTouchState.startY = e.touches[0].clientY;
      globalTouchState.startTime = Date.now();
      globalTouchState.isScrolling = false;
    },
    { passive: true }
  );

  document.addEventListener(
    "touchmove",
    (e) => {
      const deltaY = Math.abs(e.touches[0].clientY - globalTouchState.startY);
      if (deltaY > 10) {
        globalTouchState.isScrolling = true;
      }
    },
    { passive: true }
  );

  window.isUserScrolling = () => {
    const timeSinceTouch = Date.now() - globalTouchState.startTime;
    return globalTouchState.isScrolling || (timeSinceTouch < 150 && globalTouchState.startTime > 0);
  };

  window.onChangeConstructorProductSwatches = (btn, event) => {
    if (window.isUserScrolling && window.isUserScrolling()) {
      return;
    }

    if (event.type === "click") {
      event.preventDefault();
    }

    const productItem = btn.closest(".product-item");
    if (!productItem) {
      return;
    }

    const allSwatchesInProduct = productItem.querySelectorAll(".color-swatches .btn");
    allSwatchesInProduct.forEach((swatch) => swatch.classList.remove("selected"));

    btn.classList.add("selected");

    const productImg = productItem.querySelector(".product-item-img");
    if (productImg && btn.dataset.img) {
      const imgOrientation = window.theme?.productCard?.imgOrientation || "square";
      let targetWidth, targetHeight;
      switch (imgOrientation) {
        case "square":
          targetWidth = 460;
          targetHeight = 460;
          break;
        case "landscape":
          targetWidth = 200;
          targetHeight = 160;
          break;
        case "portrait":
          targetWidth = 160;
          targetHeight = 200;
          break;
        default:
          targetWidth = 460;
          targetHeight = 460;
      }

      const currentAttrWidth = parseInt(productImg.getAttribute("width"), 10);
      const currentAttrHeight = parseInt(productImg.getAttribute("height"), 10);
      if (currentAttrWidth && currentAttrHeight) {
        targetWidth = currentAttrWidth;
        targetHeight = currentAttrHeight;
      }

      const sizedSrc =
        window.constructorRecommendations?.buildSizedImageUrl(btn.dataset.img, targetWidth, targetHeight) || btn.dataset.img;

      productImg.src = sizedSrc;
      productImg.setAttribute("width", String(targetWidth));
      productImg.setAttribute("height", String(targetHeight));

      productImg.style.width = "100%";
      productImg.style.height = "auto";
      productImg.style.aspectRatio = "1/1";
      productImg.style.objectFit = "cover";
      productImg.style.objectPosition = "center";
      productImg.style.maxWidth = "100%";
      productImg.style.display = "block";

      const imgWrapper = productImg.closest(".img-wrapper");
      if (imgWrapper) {
        imgWrapper.style.overflow = "hidden";
        imgWrapper.style.position = "relative";
      }

      productImg.offsetHeight;

      productImg.addEventListener(
        "load",
        function () {
          this.setAttribute("width", String(targetWidth));
          this.setAttribute("height", String(targetHeight));
          this.style.width = "100%";
          this.style.height = "auto";
          this.style.aspectRatio = "1/1";
          this.style.objectFit = "cover";
        },
        { once: true }
      );
    }
  };

  window.showAllConstructorSwatchesInProduct = (btn, event) => {
    if (window.isUserScrolling && window.isUserScrolling()) {
      return;
    }

    if (event.type === "click") {
      event.preventDefault();
    }

    const swatchesList = btn.closest(".color-swatches");
    if (!swatchesList) {
      return;
    }

    swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
      li.removeAttribute("hidden");
    });

    btn.remove();
  };

  function initializeConstructorRecommendations() {
    const shouldInitialize =
      document.querySelector("[data-constructor-recommendations]") || window.cnstrc?.recommendationsEnabled;

    if (shouldInitialize && !window.constructorRecommendations) {
      window.constructorRecommendations = new ConstructorRecommendations();
    }
  }

  window.quickLoadRecommendations = function (selector = ".product-list", podId = "minicart-upsell") {
    const container = document.querySelector(selector);
    if (!container) {
      return;
    }

    container.closest("[data-constructor-recommendations]") || container.setAttribute("data-constructor-recommendations", podId);

    loadConstructorRecommendations(podId);
  };

  const loadConstructorRecommendations = (podId) => {
    const container = document.querySelector(`[data-constructor-recommendations="${podId}"]`);
    if (container && window.constructorRecommendations) {
      window.constructorRecommendations.loadRecommendations(podId, {
        section: container,
        numResults: parseInt(container.dataset.numResults) || 8,
      });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeConstructorRecommendations);
  } else {
    initializeConstructorRecommendations();
  }
})();
